import { useCallback, useEffect, useMemo, useState } from 'react';
import useIsMounted from '../../shared/hooks/useIsMounted';
import { fetchWithAuth } from '../auth/fetchWithAuth';
import { API_URL } from '../../config/env';

function toDraftForm(raw) {
  return {
    slug: raw?.slug || '',
    title: raw?.title || '',
    description: raw?.description || '',
    json_schema: raw?.json_schema || raw?.schema || {},
    ui_schema: raw?.ui_schema || raw?.uiSchema || {},
    defaults: raw?.defaults || {},
    meta: raw?.meta || {},
  };
}

function storageKey(formId) {
  return `maintella:form-ai-editor:${formId}`;
}

const MAX_PERSISTED_MESSAGES = 30;

function capPersistedMessages(messages) {
  if (!Array.isArray(messages) || messages.length <= MAX_PERSISTED_MESSAGES) {
    return messages;
  }
  return messages.slice(-MAX_PERSISTED_MESSAGES);
}

export default function useFormAiEditor({ formId, initialForm }) {
  const isMounted = useIsMounted();
  const [draftForm, setDraftForm] = useState(() => toDraftForm(initialForm || {}));
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState([]);
  const [lastMeta, setLastMeta] = useState(null);

  useEffect(() => {
    const initialDraft = toDraftForm(initialForm || {});
    const key = storageKey(formId);
    let hydrated = false;

    try {
      const raw = window.localStorage.getItem(key);
      if (raw) {
        const parsed = JSON.parse(raw);
        const persistedDraft = toDraftForm(parsed?.draftForm || {});
        const persistedMessages = Array.isArray(parsed?.messages)
          ? parsed.messages.filter(
              (m) =>
                m &&
                (m.role === 'user' || m.role === 'assistant') &&
                typeof m.content === 'string'
            )
          : [];

        if (persistedDraft.slug && persistedDraft.slug === initialDraft.slug) {
          setDraftForm(persistedDraft);
          setMessages(persistedMessages);
          hydrated = true;
        }
      }
    } catch (err) {
      if (!isMounted.current) return;
      console.warn('Failed to hydrate form AI editor from localStorage:', err);
    }

    if (!hydrated) {
      setDraftForm(initialDraft);
      setMessages([]);
    }
    setValidationErrors([]);
  }, [formId, initialForm, isMounted]);

  useEffect(() => {
    if (!formId) return;
    try {
      window.localStorage.setItem(
        storageKey(formId),
        JSON.stringify({
          draftForm,
          messages: capPersistedMessages(messages),
          updatedAt: new Date().toISOString(),
        })
      );
    } catch (err) {
      if (!isMounted.current) return;
      console.warn('Failed to persist form AI editor state:', err);
    }
  }, [formId, draftForm, messages, isMounted]);

  const resetDraft = useCallback(() => {
    setDraftForm(toDraftForm(initialForm || {}));
    setMessages([]);
    setValidationErrors([]);
  }, [initialForm]);

  const clearPersistedState = useCallback(() => {
    if (!formId) return;
    try {
      window.localStorage.removeItem(storageKey(formId));
    } catch (err) {
      if (!isMounted.current) return;
      console.warn('Failed to clear form AI editor localStorage state:', err);
    }
  }, [formId, isMounted]);

  const sendMessage = useCallback(
    async (text) => {
      const trimmed = text?.trim();
      if (!trimmed) return;

      setLoading(true);
      setError('');
      setValidationErrors([]);
      setMessages((prev) => [...prev, { role: 'user', content: trimmed }]);

      try {
        const response = await fetchWithAuth(
          `${API_URL}/v1/admin/forms/${formId}/ai/turn`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              message: trimmed,
              draft_form: draftForm,
              messages,
            }),
          }
        );
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.error || 'AI request failed');
        }

        if (data.assistant_text) {
          setMessages((prev) => [...prev, { role: 'assistant', content: data.assistant_text }]);
        }
        if (Array.isArray(data.validation_errors) && data.validation_errors.length > 0) {
          setValidationErrors(data.validation_errors);
        }
        if (data.applied && data.candidate_form) {
          setDraftForm(toDraftForm(data.candidate_form));
        }
        setLastMeta(data.meta || null);
      } catch (err) {
        if (!isMounted.current) return;
        setError(err.message || 'AI request failed');
      } finally {
        if (!isMounted.current) return;
        setLoading(false);
      }
    },
    [draftForm, formId, messages, isMounted]
  );

  const isDirty = useMemo(() => {
    return JSON.stringify(toDraftForm(initialForm || {})) !== JSON.stringify(draftForm);
  }, [initialForm, draftForm]);

  return {
    draftForm,
    setDraftForm,
    messages,
    sendMessage,
    loading,
    error,
    validationErrors,
    resetDraft,
    clearPersistedState,
    isDirty,
    lastMeta,
  };
}
